export const COLOR_CHART = [
  '#0397D3',
  '#426AB3',
  '#02AD35',
  '#F9BD06',
  '#FF8A00',
  '#EA4335',
];

// ========================================
// Common Color Palette
// ========================================

export const COLORS = {
  // Primary Colors
  primary: {
    main: '#426AB3',
    light: '#E3EAF9',
    dark: '#0397D3',
  },

  // Text Colors
  text: {
    primary: '#000000',
    secondary: '#426AB3',
    white: '#FFFFFF',
    gray: '#F2F2F2',
  },

  // Background Colors
  background: {
    default: '#FAFAFA',
    paper: '#FFFFFF',
    gray: '#F2F2F2',
  },

  // Semantic Colors
  success: {
    main: '#02AD35',
    light: '#A2DCB3',
    dark: '#02AD35',
  },
  error: {
    main: '#EA4335',
    light: '#F1B9BD',
    dark: '#EA4335',
  },
  warning: {
    main: '#F9BD06',
    light: '#E5C068',
    dark: '#D0945E',
  },
  info: {
    main: '#0688D3',
    light: '#A3D5ED',
    dark: '#0397D3',
  },
} as const;

// ========================================
// Alert/Notification Colors
// ========================================

export const ALERT_COLORS = {
  // Success Alert (承認待ち - Pending Approval)
  success: {
    background: '#E9FFEE',
    text: '#02AD35',
    border: '#A2DCB3',
  },

  // Error Alert (入館通知 - Entry Notification)
  error: {
    background: '#FEF1F0',
    text: '#EA4335',
    border: '#F1B9BD',
  },

  // Warning Alert (時間超過 - Overtime)
  warning: {
    background: '#FCFBE2',
    text: '#F9BD06',
    border: '#E5C068',
  },

  // Info Alert (Blue)
  info: {
    background: '#E8F6F9',
    text: '#0688D3',
    border: '#A3D5ED',
  },

  // Secondary Info (Light Blue)
  infoLight: {
    background: '#E3EAF9',
    text: '#426AB3',
    border: '#A0B2D3',
  },

  // Entry Restriction (入館制限)
  restriction: {
    background: '#FFE7CA',
    text: '#F9BD06',
    border: '#D0945E',
  },
} as const;

// ========================================
// Status Tag Colors
// ========================================

export const STATUS_COLORS = {
  // 承認待ち - Pending Approval (Green)
  pendingApproval: {
    background: '#E9FFEE',
    text: '#02AD35',
    border: '#E9FFEE',
  },

  // 入館通知 - Entry Notification (Red/Pink)
  entryNotification: {
    background: '#FEF1F0',
    text: '#EA4335',
    border: '#FEF1F0',
  },

  // 時間超過 - Overtime (Yellow)
  overtime: {
    background: '#FCFBE2',
    text: '#F9BD06',
    border: '#FCFBE2',
  },

  // Info Tag (Blue)
  infoTag: {
    background: '#E8F6F9',
    text: '#0688D3',
    border: '#E8F6F9',
  },

  // Default Tag (Light Blue)
  default: {
    background: '#E3EAF9',
    text: '#426AB3',
    border: '#E3EAF9',
  },

  // 入館制限 - Entry Restriction (Orange)
  entryRestriction: {
    background: '#FCFBE2',
    text: '#FE8B00',
    border: '#FCFBE2',
  },
} as const;

// ========================================
// Button Colors
// ========================================

export const BUTTON_COLORS = {
  primary: {
    default: {
      background: '#426AB3',
      text: '#FFFFFF',
      border: '#426AB3',
    },
    hover: {
      background: '#0397D3',
      text: '#FFFFFF',
      border: '#0397D3',
    },
  },

  secondary: {
    default: {
      background: '#FFFFFF',
      text: '#426AB3',
      border: '#426AB3',
    },
    hover: {
      background: '#E3EAF9',
      text: '#426AB3',
      border: '#426AB3',
    },
  },

  tertiary: {
    default: {
      background: 'transparent',
      text: '#426AB3',
      border: 'transparent',
    },
    hover: {
      background: '#E3EAF9',
      text: '#426AB3',
      border: 'transparent',
    },
  },

  danger: {
    default: {
      background: '#EA4335',
      text: '#FFFFFF',
      border: '#EA4335',
    },
    hover: {
      background: '#D93025',
      text: '#FFFFFF',
      border: '#D93025',
    },
  },

  text: {
    default: {
      background: 'transparent',
      text: '#426AB3',
      border: 'transparent',
    },
    hover: {
      background: 'transparent',
      text: '#0397D3',
      border: 'transparent',
    },
  },
} as const;

// ========================================
// Reception Status Colors (受付ステータス)
// ========================================

export const RECEPTION_STATUS_COLORS = {
  // 入館通知 - Entry Notification (Green)
  entryNotification: {
    background: '#02AD35',
    text: '#FFFFFF',
  },

  // 時間超過 - Overtime (Orange)
  overtime: {
    background: '#F9BD06',
    text: '#FFFFFF',
  },

  // 入館追加 - Additional Entry (Blue)
  additionalEntry: {
    background: '#426AB3',
    text: '#FFFFFF',
  },

  // Hover state (Light Blue)
  hover: {
    background: '#0397D3',
    text: '#FFFFFF',
  },
} as const;

// ========================================
// Registration Status Colors (登録ステータス)
// ========================================

export const REGISTRATION_STATUS_COLORS = {
  // 承認待ち - Pending Approval
  pendingApproval: {
    background: '#426AB3',
    text: '#FFFFFF',
  },

  // 2次承認待ち - Secondary Approval Pending
  secondaryApproval: {
    background: '#426AB3',
    text: '#FFFFFF',
  },

  // 承認制限 - Approval Restriction
  approvalRestriction: {
    background: '#EA4335',
    text: '#FFFFFF',
  },

  // 入館制限 - Entry Restriction
  entryRestriction: {
    background: '#F9BD06',
    text: '#FFFFFF',
  },

  // Hover state (Light Blue)
  hover: {
    background: '#0397D3',
    text: '#FFFFFF',
  },
} as const;

// ========================================
// Tag Colors (Chip/Badge)
// ========================================

export const TAG_COLORS = {
  // Required Tag (必須)
  required: {
    background: '#EA4335',
    text: '#FFFFFF',
    border: '#EA4335',
  },

  // Default Tag
  default: {
    background: '#E3EAF9',
    text: '#426AB3',
    border: '#A0B2D3',
  },

  // Hover state for tags
  hover: {
    background: '#E3EAF9',
    text: '#426AB3',
    border: '#426AB3',
  },
} as const;

// ========================================
// Type Definitions
// ========================================

export type AlertColorType = keyof typeof ALERT_COLORS;
export type StatusColorType = keyof typeof STATUS_COLORS;
export type ButtonColorType = keyof typeof BUTTON_COLORS;
export type ReceptionStatusType = keyof typeof RECEPTION_STATUS_COLORS;
export type RegistrationStatusType = keyof typeof REGISTRATION_STATUS_COLORS;
export type TagColorType = keyof typeof TAG_COLORS;
